import React, { useContext } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo } from "react";
import { Typography, useMediaQuery } from "@mui/material";

import StrategySuggestions from ".//StrategySuggestions";

import PageOption from "../../PageTypes/PageOption";

import Skeleton from "@mui/material/Skeleton";

import StrategySimpleDisplay from "./StrategySimpleDisplay";
import StrategyDisplay from "./StrategyDisplay";
import StrategyDialog from "./StrategyConfigurationComponents/StrategyDialog";

import PersonalizationContext from "../../PersonalizationComponents/PersonalizationContext";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";

import FormRowState from "../UtilityComponents/FormUtilityComponents/FormRowState";
import SelectedStrategy from "./SelectedStrategy";
import PeriodType from "../UtilityComponents/GraphOptionUtilityComponents/PeriodType";
import { defaultNoStrategyDisplayProps } from "./DefaultNoStrategyDisplayProps";

import StrategyCard from "./StrategyCardComponents/StrategyCard";
import AddStrategyCard from "./StrategyCardComponents/AddStrategyCard";
import { useTheme } from "@mui/material/styles";

import CallToActionBox from "../UtilityComponents/PaperUtilityComponents/CallToActionBox";
import Collapse from "@mui/material/Collapse";

import ErrorPage from "../../ErrorPageComponents/ErrorPage";
import FormConfig from "../UtilityComponents/FormUtilityComponents/FormConfig";

import ShadowPaper from "../UtilityComponents/PaperUtilityComponents/ShadowPaper";

import findNextUnusedName from "../UtilityComponents/FormUtilityComponents/findNextUnusedName";

import snackbarStrategyChange from "./snackbarStrategyChange";
import PostSelectedStrategyList from "./PostSelectedStrategyList";

import { useSearchParams } from "react-router-dom";
import { isMobile } from "react-device-detect";

const paperPadding = 2;

function getCurrentlyDisplayedStrategyDisplay(strategyList: SelectedStrategy[]) {
  if (strategyList === undefined) {
    return undefined;
  }
  const currentlyDisplayedStrategyList = strategyList.filter((strategy) => strategy.currentlyDisplayed);
  return currentlyDisplayedStrategyList.length ? currentlyDisplayedStrategyList[0] : undefined;
}

function getPrimaryStrategyWithIndex(strategyList: SelectedStrategy[]) {
  if (strategyList === undefined) {
    return {
      lockedStrategy: undefined,
      lockedIndex: -1,
    };
  }
  const strategyListWithIndexes = strategyList.map((strategy, index) => {
    return { lockedStrategy: strategy, lockedIndex: index };
  });

  const lockedInStrategy = strategyListWithIndexes.filter((strategy) => strategy.lockedStrategy.primary);
  if (lockedInStrategy.length) {
    return lockedInStrategy[0];
  }
  return {
    lockedStrategy: undefined,
    lockedIndex: -1,
  };
}

function getCurrentlyDisplayedStrategyDisplayProps(strategyList: SelectedStrategy[], nonePreSelected?: boolean) {
  if (strategyList === undefined) {
    return { ...defaultNoStrategyDisplayProps, noneSelected: true };
  }
  const currentlyDisplayedStrategyList = strategyList.filter((strategy) => strategy.currentlyDisplayed);
  return currentlyDisplayedStrategyList.length
    ? currentlyDisplayedStrategyList[0].strategyDisplayProps
    : { ...defaultNoStrategyDisplayProps, noneSelected: nonePreSelected === undefined ? true : nonePreSelected };
}

function noneSelectedFrom(strategyList: SelectedStrategy[]) {
  if (strategyList === undefined) {
    return true;
  }
  return strategyList.filter((strategy) => strategy.currentlyDisplayed).length === 0;
}

function nonePrimaryFrom(strategyList: SelectedStrategy[]) {
  if (strategyList === undefined) {
    return true;
  }
  return strategyList.filter((strategy) => strategy.primary).length === 0;
}

function selectedIsPrimaryFrom(strategyList: SelectedStrategy[]) {
  if (strategyList === undefined) {
    return false;
  }
  return strategyList.filter((strategy) => strategy.currentlyDisplayed && strategy.primary).length === 1;
}

type StrategiesPanelProps = {
  addSnackbarMessage: (title: string, message: string) => void;
  firstChecksDone: boolean;
  strategy: string;
  setStrategy: (newStrategy: string) => void;
  selectedStrategyList: SelectedStrategy[];
  setSelectedStrategyList: (newSelectedStrategyList: SelectedStrategy[]) => void;
  setStrategyMetadata: (newStrategyMetadata: { [key: string]: any }) => void;
  strategyRetrievalErrored: boolean;
  strategyOptionsConfigs: { [key: string]: FormConfig };
  strategyOptionsRowStates: { [key: string]: FormRowState[] }; // Contains information about current form values in dialog for each strategy
  setStrategyOptionsRowStates: (newStrategyOptionsRowStates: { [key: string]: FormRowState[] }) => void;
  loading: boolean;
  setLoading: (newLoading: boolean) => void;
  dialogOpen: boolean;
  setDialogOpen: (newDialogOpen: boolean) => void;
  setCloseDialogOnGraphUpdate: (newCloseDialogOnGraphUpdate: boolean) => void;
  completeLoadingOnDialogClose: boolean;
  setCompleteLoadingOnDialogClose: (newCompleteLoadingOnDialogClose: boolean) => void;
  historicalPeriod: PeriodType;
  setHistoricalPeriod: (newHistoricalPeriod: PeriodType) => void;
  lastDrawnTotalValue: number | undefined;
  setLastDrawnTotalValue: (value: number | undefined) => void;
  lastDrawnCoreStockValue: number | undefined;
  setLastDrawnCoreStockValue: (value: number | undefined) => void;
  lastDrawnOtherStockValue: number | undefined;
  setLastDrawnOtherStockValue: (value: number | undefined) => void;
  lastDrawnCashValue: number | undefined;
  setLastDrawnCashValue: (value: number | undefined) => void;
  noStockPlansAdded: boolean;
  noESPPPlansAdded: boolean;
  setPage: (newPage: PageOption) => void;
  immediateStockPlanAddPrompt: () => void;
  immediateESPPAddPrompt: () => void;
  handleUpdateNotification: () => void;
  withholdingOn: boolean;
  withholdingRate: number;
  firstWithholdingCheckDone: boolean;
};

function StrategiesPanel(props: StrategiesPanelProps) {
  const theme = useTheme();
  const fixedSizeForSideSquares = theme.spacing(50);
  // all the lifted state
  const strategy = props.strategy;
  const setStrategy = props.setStrategy;
  const selectedStrategyList = props.selectedStrategyList;
  const setSelectedStrategyList = props.setSelectedStrategyList;

  const [dialogIsNew, setDialogIsNew] = useState(true);
  const [strategyEdited, setStrategyEdited] = useState(false); // TODO: still hits backend if params changed and then changed back
  const [strategyListEditingIndex, setStrategyListEditingIndex] = useState(-1);

  const noPlansAdded = props.noStockPlansAdded && props.noESPPPlansAdded;

  // const [displayCardLoadingSymbol, setDisplayCardLoadingSymbol] = useState(false);
  const [strategyToDisplayOrEditOrAdd, setStrategyToDisplayOrEditOrAdd] = useState<
    | {
        addStrategy: string | undefined;
        editDisplayStrategy: SelectedStrategy | undefined;
        index: number;
        editDisplayAdd: "edit" | "display" | "add";
      }
    | undefined
  >(undefined);

  const disableAll = (strategyList: SelectedStrategy[]) =>
    strategyList ? strategyList.map((strategy) => ({ ...strategy, currentlyDisplayed: false })) : [];

  const demoteAll = (strategyList: SelectedStrategy[]) =>
    strategyList ? strategyList.map((strategy) => ({ ...strategy, primary: false })) : [];

  const disableAndDemoteAll = (strategyList: SelectedStrategy[]) =>
    strategyList ? strategyList.map((strategy) => ({ ...strategy, currentlyDisplayed: false, primary: false })) : [];

  function startEdit(strategy: SelectedStrategy, index: number) {
    setSelectedStrategyList([
      ...disableAll(selectedStrategyList.slice(0, index)),
      { ...selectedStrategyList[index], currentlyDisplayed: true },
      ...disableAll(selectedStrategyList.slice(index + 1)),
    ]);
    setStrategy(strategy.strategy);
    setDialogIsNew(false);
    setStrategyEdited(false);
    props.setDialogOpen(true);
    props.setStrategyOptionsRowStates({
      ...props.strategyOptionsRowStates,
      [strategy.strategy]: strategy.params,
    });
    setStrategyListEditingIndex(index);
  }

  // use search params to set lock in state on edit
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamEdit = searchParams.get("edit")?.toLowerCase();

  if (searchParamEdit === "locked_strategy") {
    setSearchParams((oldParams) => {
      oldParams.delete("edit");
      return oldParams;
    });
    const { lockedStrategy, lockedIndex } = getPrimaryStrategyWithIndex(props.selectedStrategyList);
    if (lockedIndex >= 0 && lockedStrategy !== undefined) {
      props.loading
        ? setStrategyToDisplayOrEditOrAdd({
            addStrategy: undefined,
            editDisplayStrategy: lockedStrategy,
            index: lockedIndex,
            editDisplayAdd: "edit",
          })
        : startEdit(lockedStrategy, lockedIndex);
    }
  }

  useEffect(() => {
    setSearchParams((oldParams) => {
      oldParams.delete("edit");
      return oldParams;
    });
  }, [setSearchParams]);

  useEffect(() => {
    if (!props.loading && strategyToDisplayOrEditOrAdd !== undefined) {
      if (
        strategyToDisplayOrEditOrAdd.editDisplayAdd === "edit" &&
        strategyToDisplayOrEditOrAdd.editDisplayStrategy !== undefined
      ) {
        startEdit(strategyToDisplayOrEditOrAdd.editDisplayStrategy, strategyToDisplayOrEditOrAdd.index);
      } else if (
        strategyToDisplayOrEditOrAdd.editDisplayAdd === "display" &&
        strategyToDisplayOrEditOrAdd.editDisplayStrategy !== undefined
      ) {
        setStrategy(strategyToDisplayOrEditOrAdd.editDisplayStrategy.strategy);
        setSelectedStrategyList([
          ...disableAll(selectedStrategyList.slice(0, strategyToDisplayOrEditOrAdd.index)),
          { ...selectedStrategyList[strategyToDisplayOrEditOrAdd.index], currentlyDisplayed: true },
          ...disableAll(selectedStrategyList.slice(strategyToDisplayOrEditOrAdd.index + 1)),
        ]);
      } else if (
        strategyToDisplayOrEditOrAdd.editDisplayAdd === "add" &&
        strategyToDisplayOrEditOrAdd.addStrategy !== undefined
      ) {
        handleStrategyAdd(strategyToDisplayOrEditOrAdd.addStrategy);
      }
      setStrategyToDisplayOrEditOrAdd(undefined);
    }
  }, [props.loading]);

  function handleStrategyAdd(newStrategy?: string) {
    if (newStrategy !== undefined) {
      setStrategy(newStrategy);
    }

    const strategyToAdd = newStrategy || strategy;
    const usedNames = selectedStrategyList.map(
      (ss) => ss.params[0].currentModeStates[ss.params[0].currentModeIndex] as string
    );

    const candidateName =
      (props.strategyOptionsRowStates[strategyToAdd] &&
        props.strategyOptionsRowStates[strategyToAdd][0] &&
        (props.strategyOptionsRowStates[strategyToAdd][0].currentModeStates[0] as string)) ||
      strategyToAdd;

    const overrideNickname = usedNames.includes(candidateName)
      ? findNextUnusedName(strategyToAdd, usedNames)
      : candidateName;

    if (props.strategyOptionsRowStates[strategyToAdd] !== undefined) {
      const obj = {
        ...props.strategyOptionsRowStates,
        [strategyToAdd]: props.strategyOptionsRowStates[strategyToAdd].map((rowState, index) =>
          index === 0
            ? { currentModeIndex: 0, currentModeStates: [overrideNickname], currentModeErrors: [null] }
            : { ...rowState }
        ),
      };
      props.setStrategyOptionsRowStates(obj);
    }

    setDialogIsNew(true);
    props.setDialogOpen(true);
  }

  const maxPortfolioValues =
    selectedStrategyList === undefined
      ? undefined
      : Math.max(...selectedStrategyList.map((s) => s.strategyDisplayProps.maxPortfolioValue || 0));

  const strategyDisplayProps = {
    ...getCurrentlyDisplayedStrategyDisplayProps(
      selectedStrategyList,
      strategyToDisplayOrEditOrAdd === undefined || strategyToDisplayOrEditOrAdd.editDisplayAdd === "add"
    ),
    historicalPeriod: props.historicalPeriod,
    setHistoricalPeriod: props.setHistoricalPeriod,
    lastDrawnTotalValu: props.lastDrawnTotalValue,
    setLastDrawnTotalValue: props.setLastDrawnTotalValue,
    lastDrawnCoreStockValue: props.lastDrawnCoreStockValue,
    setLastDrawnCoreStockValue: props.setLastDrawnCoreStockValue,
    lastDrawnOtherStockValue: props.lastDrawnOtherStockValue,
    setLastDrawnOtherStockValue: props.setLastDrawnOtherStockValue,
    lastDrawnCashValue: props.lastDrawnCashValue,
    setLastDrawnCashValue: props.setLastDrawnCashValue,
    maxPortfolioValue: maxPortfolioValues,
    strategyOptions: props.strategyOptionsConfigs,
    spinAddStrategy:
      props.loading &&
      strategyToDisplayOrEditOrAdd !== undefined &&
      strategyToDisplayOrEditOrAdd.editDisplayAdd === "add"
        ? strategyToDisplayOrEditOrAdd.addStrategy
        : undefined,
    handleAdd: (strategy: string | undefined) => {
      if (props.loading) {
        setStrategyToDisplayOrEditOrAdd({
          addStrategy: strategy,
          editDisplayStrategy: undefined,
          index: -1,
          editDisplayAdd: "add",
        });
      } else {
        handleStrategyAdd(strategy);
      }
    },
  };

  const { personalizationConfig, setPersonalizationConfig } = useContext(PersonalizationContext);
  const isSimple = "complexity" in personalizationConfig && personalizationConfig.complexity === "simple";

  const mainDisplayContents = isSimple ? (
    <StrategySimpleDisplay {...strategyDisplayProps} />
  ) : (
    <StrategyDisplay {...strategyDisplayProps} />
  );

  const sortedStrategyValues = selectedStrategyList
    .map((strategy) => strategy.strategyDisplayProps.totalValue)
    .sort((a, b) => b - a);

  const strategyCardsStackContents =
    selectedStrategyList &&
    selectedStrategyList.map((strategy, index) => (
      <StrategyCard
        key={index}
        supratitle={strategy.strategy}
        strategyRetrievalErrored={props.strategyRetrievalErrored}
        // Assumes that the nickname for the strategy is the 0th param in the form params
        title={
          (strategy !== undefined &&
            strategy.params &&
            strategy.params[0] &&
            (strategy.params[0].currentModeStates[strategy.params[0].currentModeIndex] as string)) ||
          strategy.strategy
        }
        strategyDisplayProps={strategy.strategyDisplayProps}
        description={
          (props.strategyOptionsConfigs[strategy.strategy] &&
            props.strategyOptionsConfigs[strategy.strategy].description) ||
          ""
        }
        selected={strategy.currentlyDisplayed}
        primary={strategy.primary}
        onPrimaryClick={() => {
          if (props.loading) {
            return;
          } else {
            setStrategy(strategy.strategy);
            const newSelectedStrategyList = [
              ...disableAndDemoteAll(selectedStrategyList.slice(0, index)),
              { ...strategy, currentlyDisplayed: true, primary: true },
              ...disableAndDemoteAll(selectedStrategyList.slice(index + 1)),
            ];
            setSelectedStrategyList(newSelectedStrategyList);
            snackbarStrategyChange(props.addSnackbarMessage, strategy);
            PostSelectedStrategyList(
              newSelectedStrategyList,
              props.setStrategyMetadata,
              props.handleUpdateNotification
            );
          }
        }}
        onCardClick={() => {
          if (props.loading) {
            setStrategyToDisplayOrEditOrAdd({
              addStrategy: undefined,
              editDisplayStrategy: strategy,
              index: index,
              editDisplayAdd: "display",
            });
          } else if (strategy.currentlyDisplayed) {
            startEdit(strategy, index);
          } else {
            setStrategy(strategy.strategy);
            setSelectedStrategyList([
              ...disableAll(selectedStrategyList.slice(0, index)),
              { ...strategy, currentlyDisplayed: true },
              ...disableAll(selectedStrategyList.slice(index + 1)),
            ]);
          }
        }}
        onEditClick={() =>
          props.loading
            ? setStrategyToDisplayOrEditOrAdd({
                addStrategy: undefined,
                editDisplayStrategy: strategy,
                index: index,
                editDisplayAdd: "edit",
              })
            : startEdit(strategy, index)
        }
        loading={
          strategyToDisplayOrEditOrAdd !== undefined &&
          strategyToDisplayOrEditOrAdd.editDisplayAdd !== "add" &&
          strategyToDisplayOrEditOrAdd.index === index
        }
        ranking={
          selectedStrategyList.length === 0 || props.loading
            ? undefined
            : sortedStrategyValues.indexOf(strategy.strategyDisplayProps.totalValue) + 1
        }
      />
    ));

  // TODO: fix the loading behavior on delete so it doesn't skeleton forever
  const strategyCardsStackAddCard = (
    <AddStrategyCard
      currentStrategy={strategy}
      currentDescription={
        (props.strategyOptionsConfigs[strategy] && props.strategyOptionsConfigs[strategy].description) || ""
      }
      setStrategy={setStrategy}
      strategyOptions={Object.keys(props.strategyOptionsConfigs)}
      handleInfoClick={() => props.setSelectedStrategyList(disableAll(props.selectedStrategyList))}
      handleAdd={() => handleStrategyAdd(strategy)}
      loading={props.loading}
    />
  );

  function handleAddOrSaveGenerator(
    strategy: string,
    dialogIsNew: boolean,
    isPrimary: boolean,
    strategyEdited: boolean
  ) {
    // does the special suggestions if strategy is 'SUGGESTED'
    function handleAddOrSave(state: FormRowState[]) {
      props.setLoading(true);
      var preStrategyList;
      var postStrategyList: SelectedStrategy[];

      const needToUpdateFromBackend = dialogIsNew || strategyEdited;

      if (dialogIsNew) {
        preStrategyList = disableAll(selectedStrategyList);
        postStrategyList = [];
      } else {
        preStrategyList = disableAll(selectedStrategyList.slice(0, strategyListEditingIndex));
        postStrategyList = disableAll(selectedStrategyList.slice(strategyListEditingIndex + 1));
      }

      var addedOrSavedStrategy = [];

      if (strategy === "SUGGESTED") {
        const strategySuggestions = StrategySuggestions(selectedStrategyList);
        addedOrSavedStrategy = strategySuggestions.suggestions;
        if (strategySuggestions.snackbarPrimaryName !== undefined) {
          snackbarStrategyChange(props.addSnackbarMessage, strategySuggestions.snackbarPrimaryName);
        }
      } else {
        addedOrSavedStrategy = [
          {
            strategy: strategy,
            params: state,
            currentlyDisplayed: true,
            primary: !dialogIsNew && isPrimary,
            strategyDisplayProps: needToUpdateFromBackend
              ? defaultNoStrategyDisplayProps
              : { ...selectedStrategyList[strategyListEditingIndex].strategyDisplayProps }, // copy over graph if no need to update
          },
        ];
      }

      const newSelectedStrategyList = [...preStrategyList, ...addedOrSavedStrategy, ...postStrategyList];
      setSelectedStrategyList(newSelectedStrategyList); // TODO; change the action items from this?
      PostSelectedStrategyList(newSelectedStrategyList, props.setStrategyMetadata, props.handleUpdateNotification);

      if (needToUpdateFromBackend) {
        props.setCloseDialogOnGraphUpdate(true);
      } else {
        props.setDialogOpen(false);
      }
      // TODO: reset the rowStateList in StrategyDialog
    }
    return handleAddOrSave;
  }

  const strategyDialog =
    strategy === "" ? null : (
      <StrategyDialog
        dialogTitle={(dialogIsNew ? "Configure " : "Edit ") + strategy}
        description={
          (props.strategyOptionsConfigs[strategy] && props.strategyOptionsConfigs[strategy].description) || ""
        }
        open={props.dialogOpen}
        loading={props.loading}
        config={props.strategyOptionsConfigs[strategy]}
        rowStateList={props.strategyOptionsRowStates[strategy]}
        disallowedNames={
          selectedStrategyList === undefined
            ? []
            : dialogIsNew
            ? selectedStrategyList.map((ss) => ss.params[0].currentModeStates[ss.params[0].currentModeIndex] as string)
            : selectedStrategyList
                .filter((ss, index) => index !== strategyListEditingIndex)
                .map((ss) => ss.params[0].currentModeStates[ss.params[0].currentModeIndex] as string)
        }
        setRowStateList={(newRowStateList) => {
          setStrategyEdited(true);
          props.setStrategyOptionsRowStates({
            ...props.strategyOptionsRowStates,
            [strategy]:
              typeof newRowStateList === "function"
                ? newRowStateList(props.strategyOptionsRowStates[strategy])
                : newRowStateList,
          });
        }}
        handleCancel={() => {
          if (!props.loading) props.setDialogOpen(false);
        }}
        isNew={dialogIsNew}
        isPrimary={
          !dialogIsNew &&
          selectedStrategyList &&
          strategyListEditingIndex >= 0 &&
          strategyListEditingIndex < selectedStrategyList.length &&
          selectedStrategyList[strategyListEditingIndex].primary
        }
        handleAddOrSave={handleAddOrSaveGenerator(
          strategy,
          dialogIsNew,
          !dialogIsNew &&
            strategyListEditingIndex >= 0 &&
            strategyListEditingIndex < selectedStrategyList.length &&
            selectedStrategyList[strategyListEditingIndex].primary,
          !dialogIsNew && strategyEdited
        )}
        handleDelete={() => {
          props.setLoading(true);
          const newSelectedStrategyList = [
            ...disableAll(selectedStrategyList.slice(0, strategyListEditingIndex)),
            ...disableAll(selectedStrategyList.slice(strategyListEditingIndex + 1)),
          ];
          setSelectedStrategyList(newSelectedStrategyList);
          PostSelectedStrategyList(newSelectedStrategyList, props.setStrategyMetadata, props.handleUpdateNotification);

          props.setCloseDialogOnGraphUpdate(true);
        }}
        completeLoadingOnDialogClose={props.completeLoadingOnDialogClose}
        setCompleteLoadingOnDialogClose={props.setCompleteLoadingOnDialogClose}
        setLoading={props.setLoading}
      />
    );
  const taxSavings = getCurrentlyDisplayedStrategyDisplayProps(selectedStrategyList).taxSavings;

  const noStrategies = selectedStrategyList === undefined || selectedStrategyList.length === 0;

  const noneSelected = useMemo(() => noneSelectedFrom(selectedStrategyList), [selectedStrategyList]);

  useEffect(() => {
    if (noneSelected) {
      props.setLastDrawnTotalValue(undefined);
      props.setLastDrawnCoreStockValue(undefined);
      props.setLastDrawnOtherStockValue(undefined);
      props.setLastDrawnCashValue(undefined);
    }
  }, [noneSelected]);

  const selectedIsPrimary = useMemo(() => selectedIsPrimaryFrom(selectedStrategyList), [selectedStrategyList]);

  const lgMediaQuery = useMediaQuery(theme.breakpoints.down("lg"));

  const strategyCardStackDesktop = (
    <Stack direction="row" sx={{ py: 2, overflowX: "auto", flexShrink: 0, gap: 2 }}>
      {strategyCardsStackAddCard}
      {strategyCardsStackContents}
    </Stack>
  );

  const strategyCardStackMobile = (
    <>
      {strategyCardsStackAddCard}
      <Stack direction="row" sx={{ py: 2, overflowX: "auto", flexShrink: 0, gap: 1 }}>
        {strategyCardsStackContents}
      </Stack>
    </>
  );

  const strategyCardStack = isMobile ? strategyCardStackMobile : strategyCardStackDesktop;

  return (
    <Stack
      sx={{
        display: "flex",
        height: "100%",
        flexDirection: { xs: isMobile ? "column" : "column-reverse", lg: "row" },
        gap: isMobile ? 0 : 2,
      }}
    >
      {strategyDialog}
      <Stack
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minWidth: 0,
          justifyContent: "flex-start",
          gap: isMobile ? 0 : 2,
        }}
      >
        {isMobile ? null : (
          <ShadowPaper
            PaperProps={{
              className: "highlight-for-strategies-tour-step-1 highlight-for-strategies-tour-step-2",
              sx: {
                flexGrow: isSimple ? 0 : 1,
                sizing: "border-box",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,
                position: "relative",
              },
            }}
            locked={props.firstChecksDone && noPlansAdded && props.firstChecksDone && !noneSelected}
            padding={paperPadding}
            unlockButtonText="Add a Stock Plan or ESPP Plan to unlock the historical Strategy Plot"
            handleUnlock={() => {
              props.immediateStockPlanAddPrompt();
              props.setPage("stock-plans");
            }}
          >
            {props.firstChecksDone ? (
              props.strategyRetrievalErrored && !noPlansAdded ? (
                <ErrorPage checkYourInputsandRefresh />
              ) : (
                mainDisplayContents
              )
            ) : (
              <Skeleton variant="rectangular" sx={{ height: "100%" }} />
            )}
          </ShadowPaper>
        )}
        {noStrategies ? null : strategyCardStack}
      </Stack>
      <Stack
        sx={{
          display: "flex",
          flexDirection: { xs: "row", lg: "column" },
          justifyContent: "flex-start",
          gap: 0,
          flexShrink: { xs: 1, lg: 0 },
          overflow: "auto",
        }}
      >
        {!props.firstChecksDone ? (
          <Paper
            sx={{
              width: fixedSizeForSideSquares,
              flexShrink: 0,
              padding: paperPadding,
              marginBottom: { xs: 0, lg: 2 },
              marginRight: { xs: 2, lg: 0 },
              boxSizing: "border-box",
            }}
          >
            <Skeleton variant="rectangular" width="100%" height={fixedSizeForSideSquares} />
          </Paper>
        ) : (
          [
            {
              inCondition: props.firstChecksDone && noPlansAdded,
              boxProps: {
                title: "Have stock plans?",
                description:
                  "Enter some basic information about your stock plans to use the powerful modeling tools in this app.",
                buttonText: "Configure your Stock Plans",
                urgent: true,
                onClick: () => {
                  props.immediateStockPlanAddPrompt();
                  props.setPage("stock-plans");
                },
              },
            },
            {
              inCondition: noPlansAdded,
              boxProps: {
                title: "Have ESPP plans?",
                description:
                  "Enter some basic information about your ESPPs to use the powerful modeling tools in this app.",
                buttonText: "Configure your ESPPs",
                urgent: true,
                onClick: () => {
                  props.immediateESPPAddPrompt();
                  props.setPage("espps");
                },
              },
            },
            {
              inCondition:
                !noPlansAdded &&
                (selectedStrategyList === undefined || selectedStrategyList.length < 1) &&
                props.firstChecksDone,
              boxProps: {
                title: "Not sure where to start?",
                description:
                  "Making decisions about your stock plan can be hard. It can help to see how various strategies could have impacted your holdings over time. Click here to add some suggestions to get you started.",
                buttonText: noPlansAdded ? "" : "Add example strategies",
                loading: props.loading,
                onClick: () => handleAddOrSaveGenerator("SUGGESTED", true, false, false)([]),
              },
            },
            {
              inCondition: !noStrategies && props.firstChecksDone,
              boxProps: {
                title: noneSelected
                  ? "Interested in reminders?"
                  : selectedIsPrimary
                  ? "This strategy is locked in!"
                  : "Want reminders to execute this?",
                description: nonePrimaryFrom(selectedStrategyList)
                  ? "Now that you've added a strategy option to explore in the past. Lock in your favorite for the future, and we'll automatically notify you about the trades that align with your goals!"
                  : noneSelected
                  ? "Select a strategy option below or add some more to explore further!"
                  : selectedIsPrimary
                  ? "This strategy is locked in! You'll get automatic reminders to execute this strategy in the future."
                  : "Like what you see here better? Lock in to switch your selected trading strategy to this option.",
                disabled: noneSelected || selectedIsPrimary,
                buttonText: noneSelected
                  ? "Select a strategy first"
                  : selectedIsPrimary
                  ? "Already locked in"
                  : "Lock In Current Strategy",
                onClick: () => {
                  const newSelectedStrategyList = selectedStrategyList.map((strategy) => ({
                    ...strategy,
                    primary: strategy.currentlyDisplayed, // makes the current one primary
                  }));
                  setSelectedStrategyList(newSelectedStrategyList);
                  const currentStrat = getCurrentlyDisplayedStrategyDisplay(selectedStrategyList);
                  if (currentStrat !== undefined) {
                    snackbarStrategyChange(props.addSnackbarMessage, currentStrat);
                  }
                  PostSelectedStrategyList(
                    newSelectedStrategyList,
                    props.setStrategyMetadata,
                    props.handleUpdateNotification
                  );
                },
              },
            },
            {
              inCondition: props.firstWithholdingCheckDone,
              boxProps: {
                title: props.withholdingOn ? "Tax withholding" : "Need to set up tax withholding?",
                description: props.withholdingOn
                  ? "You have tax withholding set up and enabled. All values are net of " +
                    props.withholdingRate.toLocaleString("en-US", { style: "percent", maximumFractionDigits: 0 }) +
                    " tax withholding. These shares never end up in your hands, so our models remove them from all values."
                  : "It looks like you either haven't set up tax withholding or have it disabled. Models and values will appear high without accurate tax withholding in your profile.",
                disabled: false,
                buttonText: props.withholdingOn ? "Edit tax withholding" : "Set up tax withholding",
                onClick: () => props.setPage("profile"),
              },
            },
            {
              inCondition: !noStrategies && props.firstChecksDone,
              boxProps: {
                title: "Selling some shares?",
                description:
                  "Use smart lot selection to minimize your tax burden. We'll identify which shares to sell that minimize your tax burden." +
                  (taxSavings <= 0
                    ? ""
                    : " Smart lot selection could have saved you $" +
                      Number(Number(taxSavings).toFixed(0)).toLocaleString("en-US") +
                      " on your selected strategy."),
                disabled: true,
                buttonText: "(Coming Soon)",
                onClick: () => console.log("TODO"),
              },
            },
          ].map(
            (
              box // TODO: we can choose to show only the first 3 of these or something...factor this out into a function?
            ) => (
              <Collapse
                key={box.boxProps.title}
                in={box.inCondition}
                orientation={lgMediaQuery ? "horizontal" : "vertical"}
                sx={{
                  flexShrink: 0,
                }}
              >
                <Paper
                  sx={{
                    width: fixedSizeForSideSquares,
                    flexShrink: 0,
                    padding: paperPadding,
                    marginBottom: { xs: 0, lg: 2 },
                    marginRight: { xs: 2, lg: 0 },
                    boxSizing: "border-box",
                  }}
                >
                  <CallToActionBox {...box.boxProps} />
                </Paper>
              </Collapse>
            )
          )
        )}
        {/* // TODO: Have a column with summary stats about them?...e.g. best last year....most value...most stock value...most cash value...highest sharpe ratio */}
      </Stack>
    </Stack>
  );
}

export default StrategiesPanel;
